import React, { useState, useEffect } from 'react';
import { ChevronLeft, Copy, ClipboardCheck } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../../utils/axiosInterceptor';
import toast from 'react-hot-toast';

export const getSessionStatus = (status: string) => {
  let statusText = status;
  let statusColor = "bg-gray-100 text-gray-800";

  if(status === 'terminated'){
    statusColor = "bg-red-100 text-red-800";
    statusText = "Terminated";
  }else if(status === 'completed'){
    statusColor = "bg-green-100 text-green-800";
    statusText = "Completed";
  }else if(status === 'in_progress'){
    statusColor = "bg-blue-100 text-blue-800";
    statusText = "In Progress";
  }else if(status === 'paused'){
    statusColor = "bg-yellow-100 text-yellow-800";
    statusText = "Paused";
  }else if(status === 'failed'){
    statusColor = "bg-red-100 text-red-800";
    statusText = "Failed";
  }else if(status === 'invitation_sent'){
    statusColor = "bg-yellow-100 text-yellow-800";
    statusText = "Invitation Sent";
  }
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColor}`}>{statusText}</span>

 
}

const CompletedInterviews: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const assessmentId = params.id;
  const [completedInterviews, setCompletedInterviews] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (assessmentId) {
      fetchCompletedInterviews();
    }
  }, [assessmentId]);

  const fetchCompletedInterviews = async () => {
    setLoading(true);
    try {
      // Fetch candidates for this assessment
      const candidatesResponse = await axiosInstance.get(`/candidates/${assessmentId}/candidates`);
      const candidates = candidatesResponse.data.data || [];
      
      // Filter only completed candidates and map to interview format
      const completed = candidates
        .map((candidate: any) => ({
          _id: candidate._id,
          candidateName: candidate.canidate || 'N/A',
          email: candidate.email || 'N/A',
          phone: candidate.phone || 'N/A',
          status: candidate.status || 'N/A',
          createdAt: candidate.createdAt,
          takenOn: candidate.createdAt || candidate.updatedAt || new Date(),
          sessionId: candidate.sessionId || 'N/A',
          assessmentId: candidate.assessmentId || 'N/A',
          candidateId: candidate.candidateId || 'N/A',
        }));
      
      setCompletedInterviews(completed);
    } catch (error) {
      console.error('Error fetching completed interviews:', error);
      setCompletedInterviews([]);
    } finally {
      setLoading(false);
    }
  };

 

  const formatDate = (date: string | Date) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <button
          onClick={() => {
            navigate('/dashboard');
          }}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ChevronLeft className="h-5 w-5" />
          <span>Back</span>
        </button>
        
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Completed Interviews</h2>
          <p className="text-gray-600 mt-1">View all completed interviews for this assessment</p>
        </div>

        {loading ? (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-500 mt-4">Loading interviews...</p>
          </div>
        ) : completedInterviews.length > 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Candidate</th>
                    <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Email</th>
                    <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Phone</th>
                    <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Status</th>
                    <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Taken On</th>
                    <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {completedInterviews.map((interview, index) => (
                    <tr key={interview._id || index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                      <td className="py-4 px-6 text-sm text-gray-900">{interview.candidateName}</td>
                      <td className="py-4 px-6 text-sm text-gray-900">{interview.email}</td>
                      <td className="py-4 px-6 text-sm text-gray-900">{interview.phone}</td>
                      <td className="py-4 px-6">
                        {getSessionStatus(interview.status)}
                       
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-900">{formatDate(interview.takenOn)}</td>
                      <td className="py-4 px-6">
                        <div className="flex items-center space-x-3">
                          <button 
                            className="text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors"
                            onClick={() => {
                              // Navigate to details page with candidate and assessment IDs
                              navigate(`/details?sessionId=${interview.sessionId}&assessmentId=${assessmentId}`);
                            }}
                          >
                            View Details
                          </button>
                          <button 
                            className="text-gray-600 hover:text-gray-900 transition-colors p-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              // Copy candidate details to clipboard
                              const details = `${process.env.REACT_APP_FRONTEND_URL}/interview?assessmentId=${interview.assessmentId}&candidateId=${interview.candidateId}&sessionId=${interview.sessionId}`;
                              navigator.clipboard.writeText(details).then(() => {
                                toast.success('interview url copied to clipboard');
                              }).catch(() => {
                                toast.error('Failed to copy details');
                              });
                            }}
                            title="Copy details"
                          >
                            <Copy className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <ClipboardCheck className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Completed Interviews</h3>
            <p className="text-gray-600">
              No interviews have been completed for this assessment yet.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompletedInterviews;
